<template>
  <el-tooltip
    :popper-class="[$style.TradeSetting__Tooltip, $style[`TradeSetting__Tooltip--${setting?.theme}`]].join(' ')"
    v-model="showTradeNavigatorTooltip"
    :manual="true"
    placement="bottom-end"
  >
    <template #content>
      <span
        ><span>{{ $t('trade-navigator.setting_tooltip') }}</span
        ><r-icon
          :class="$style.TradeSetting__TooltipClose"
          name="icon-actions_navigation_close"
          :size="12"
          @click.native="onCloseTradeNavigatorTooltip"
      /></span>
    </template>
    <el-popover
      v-if="project === PROJECT.MASTER"
      placement="bottom"
      trigger="hover"
      :visible-arrow="false"
      :width="268"
      :popper-class="
        [
          $style.TradeSetting__Popper,
          $style[`TradeSetting--${setting?.theme}`],
          $style[`TradeSetting__Popper--${size}`],
        ].join(' ')
      "
    >
      <template #reference>
        <div @click="onCloseTradeNavigatorTooltip">
          <slot />
        </div>
      </template>
      <setting-pane
        :setting="setting"
        :project="project"
        :active-tab="SettingTab.STYLE"
        @onChange="ctx => $emit('onChange', ctx)"
      />
    </el-popover>
    <drawer
      position="right"
      :overlay="true"
      :animation-duration="0.375"
      :active="drawerActive"
      @change="v => drawerActive = v"
      :class="$style[`TradeSetting--${setting?.theme}`]"
    >
      <button :class="$style.TradeSetting__DrawerBtn" @click="() => drawerActive = true">
        <slot />
      </button>
      <div :class="$style.TradeSetting__DrawerPane" slot="content">
        <div :class="$style.TradeSetting__DrawerHead">
          <h3 :class="$style.TradeSetting__DrawerTitle">
            <span
              :class="{
                [$style.TradeSetting__DrawerTitleBtn]: true,
                [$style['TradeSetting__DrawerTitleBtn--active']]: activeTab === SettingTab.PREFERENCE,
              }"
              @click="activeTab = SettingTab.PREFERENCE"
            >
              {{$t('trade-navigator.preference')}}
            </span>
            <span
              :class="{
                [$style.TradeSetting__DrawerTitleBtn]: true,
                [$style['TradeSetting__DrawerTitleBtn--active']]: activeTab === SettingTab.STYLE,
              }"
              @click="activeTab = SettingTab.STYLE"
            >
              {{$t('trade-navigator.style')}}
            </span>
          </h3>
          <button :class="$style.TradeSetting__CloseBtn" @click="() => drawerActive = v">
            <i class="el-icon-close" />
          </button>
        </div>
        <setting-pane
          :setting="setting"
          :project="project"
          :active-tab="activeTab"
          @onChange="ctx => $emit('onChange', ctx)"
        >
          <template #preference>
            <slot name="preference" />
          </template>
          <template #layoutConfig>
            <slot name="layoutConfig" />
          </template>
        </setting-pane>
      </div>
    </drawer>
  </el-tooltip>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import messages from '../TradeNavigator/i18n';
import Drawer from '../Drawer/index.vue';
import SettingPane, { SettingTab } from './SettingPane.vue';
import { PROJECT } from '~/frontend-shared/helper/util';

export default defineComponent({
	name: 'TradeSetting',
	i18n: {
		messages,
	},
	emits: ['onChange'],
	props: {
		setting: Object,
		project: {
			validator(value) {
				return [PROJECT.MASTER, PROJECT.CONTRACT].includes(value);
			},
			default: PROJECT.MASTER,
		},
		size: String,
	},
	components: {
    Drawer,
    SettingPane,
	},

	setup(props, context) {

    const showTradeNavigatorTooltip = ref(false);
    const drawerActive = ref(false);
    const activeTab = ref(SettingTab.PREFERENCE);

    onMounted(() => {
			// 设置初始值
			// 如果已经存在值，则不进行初始化
			if (!window.localStorage.getItem('showTradeNavigatorTooltip')) {
				showTradeNavigatorTooltip.value = JSON.parse(window.localStorage.getItem('showTradeNavigatorTooltip')) || true;
			}
		});

		return {
			PROJECT,
      drawerActive,
      showTradeNavigatorTooltip,
      activeTab,
      SettingTab,
      onCloseTradeNavigatorTooltip() {
        // 如果已经存在值，则不再展示
        if (window.localStorage.getItem('showTradeNavigatorTooltip')) return;

        showTradeNavigatorTooltip.value = false;

        window.localStorage.setItem('showTradeNavigatorTooltip', JSON.stringify(showTradeNavigatorTooltip.value));
      }
		};
	},
});
</script>

<style module lang="less">
.TradeSetting {
	&--dark {
		--TradeSetting-background: #282c37;
		--TradeSetting-divide: #373d4e;
		--TradeSetting-text-color: #fff;
		--TradeSetting-secondary-color: #a3a8b7;
		--TradeSetting-Btn-text: #a3a8b7;
		--TradeSetting-Btn-border: #373d4e;
		--TradeSetting-switch-background: #373d4e;
	}
	&--light {
		--TradeSetting-background: #fff;
		--TradeSetting-divide: #f3f8fa;
		--TradeSetting-text-color: #1a1b1f;
    --TradeSetting-secondary-color: #6A7C81;
		--TradeSetting-Btn-text: #1a1b1f;
		--TradeSetting-Btn-border: #cacfd7;
    --TradeSetting-switch-background: #e1e9ec;
	}

	&__Popper {
		padding: 0 16px;
		border: 0 none;
		box-sizing: border-box;
		background-color: var(--TradeSetting-background);
		top: 68px !important;
		margin-top: 0 !important;
	}

	&__Popper--small {
		top: 56px !important;
	}

  &__Tooltip {
    &--dark {
      --trade-navigator-tooltip-background: #51586a;
      --trade-navigator-tooltip-text: #fff;
    }
    &--light {
      --trade-navigator-tooltip-background: #9cb3ba;
      --trade-navigator-tooltip-text: #fff;
    }
    margin-left: 4px !important;
    z-index: 10086 !important; // 提高tooltip到最高层级
    color: var(--trade-navigator-tooltip-text) !important;
    background-color: var(--trade-navigator-tooltip-background) !important;
    white-space: nowrap;
    :global(.popper__arrow) {
      border-bottom-color: var(--trade-navigator-tooltip-background) !important;
      &::after {
        border-bottom-color: var(--trade-navigator-tooltip-background) !important;
      }
    }
  }
  &__TooltipClose {
		margin-left: 8px;
		cursor: pointer;
	}
  &__DrawerBtn {
    padding: 0;
    height: 100%;
    background: none;
    border: 0 none;
  }
  &__DrawerPane {
    padding: 16px;
    width: 300px;
    height: 100%;
    background-color: var(--TradeSetting-background);
    box-sizing: border-box;
  }
  &__DrawerHead {
    display: flex;
    margin-bottom: 12px;
    justify-content: space-between;
    align-items: center;
  }
  &__DrawerTitle {
    display: flex;
    color: var(--TradeSetting-text-color);
    font-size: 20px;
    gap: 12px;
    &Btn {
      cursor: pointer;
      color: var(--TradeSetting-secondary-color);
      font-weight: 400;
      &--active {
        font-weight: 500;
        color: var(--TradeSetting-text-color);
      }
    }
  }
  &__CloseBtn {
    background: none;
    border: 0 none;
    cursor: pointer;
    color: var(--TradeSetting-text-color);
  }
}
</style>
